import { THEME } from "@/enums/profile";
import {
  buildPreferenceCookieWrite,
  getPreferenceCookieAttributes,
} from "@/utils/common/preferenceCookie";
import { LANGUAGE_COOKIE_NAME } from "@/utils/common/languageCookie";

/**
 * UI SSR theme preference cookie. Host-only (no Domain) — not sent to
 * Galactus (`NEXT_PUBLIC_GAIL_API` / api.meetgail.com).
 */
export const THEME_COOKIE_NAME = "gail_theme";

const THEME_VALUES = new Set<string>([THEME.LIGHT, THEME.DARK, THEME.SYSTEM]);

/**
 * Normalize a raw cookie / storage value to a THEME preference, or `null`
 * when missing / unsupported.
 */
export const parseThemeCookieValue = (
  value: string | null | undefined
): THEME | null => {
  if (value == null || value.trim() === "") return null;
  const normalized = value.trim().toLowerCase();
  if (!THEME_VALUES.has(normalized)) return null;
  return normalized as THEME;
};

/**
 * Read `gail_theme` from a raw Cookie header string (server-side).
 */
export const readThemeFromCookieHeader = (
  cookieHeader: string | undefined
): THEME | null => {
  if (!cookieHeader) return null;
  const match = cookieHeader
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${THEME_COOKIE_NAME}=`));
  if (!match) return null;
  const raw = decodeURIComponent(match.slice(THEME_COOKIE_NAME.length + 1));
  return parseThemeCookieValue(raw);
};

/**
 * Read `gail_theme` from `document.cookie` (client-side). Returns `null`
 * during SSR when `document` is unavailable.
 */
export const readThemeFromDocumentCookie = (): THEME | null => {
  if (globalThis.document === undefined) return null;
  return readThemeFromCookieHeader(globalThis.document.cookie);
};

const readThemeFromLegacyLocalStorage = (): THEME | null => {
  if (globalThis.window === undefined) return null;
  try {
    const direct = parseThemeCookieValue(
      globalThis.localStorage.getItem("theme")
    );
    if (direct) return direct;

    const cached = globalThis.localStorage.getItem("userProfile");
    if (!cached) return null;
    const parsed = JSON.parse(cached) as { theme?: string };
    return parseThemeCookieValue(parsed?.theme);
  } catch {
    return null;
  }
};

/**
 * Persist the theme preference so the next SSR request and the client's
 * first paint agree (avoids Light→Dark flashes and React #418 on the
 * profile theme Select).
 */
export const writeThemeCookie = (theme: THEME): void => {
  if (globalThis.document === undefined) return;
  globalThis.document.cookie = buildPreferenceCookieWrite(
    THEME_COOKIE_NAME,
    theme
  );
};

/**
 * Initial theme preference: cookie first, then one-time migrate from
 * localStorage, else light.
 */
export const getInitialThemePreference = (): THEME => {
  const fromCookie = readThemeFromDocumentCookie();
  if (fromCookie) return fromCookie;

  const fromLegacy = readThemeFromLegacyLocalStorage();
  if (fromLegacy) {
    writeThemeCookie(fromLegacy);
    return fromLegacy;
  }

  return THEME.LIGHT;
};

/**
 * Resolve a theme preference to a concrete `data-theme` value.
 * `auto` uses the system preference on the client; on the server it falls
 * back to light (a blocking head script corrects before first paint).
 */
export const resolveDomTheme = (
  preference: THEME
): THEME.LIGHT | THEME.DARK => {
  if (preference === THEME.DARK) return THEME.DARK;
  if (preference === THEME.LIGHT) return THEME.LIGHT;

  if (globalThis.window !== undefined) {
    try {
      if (globalThis.window.matchMedia("(prefers-color-scheme: dark)").matches) {
        return THEME.DARK;
      }
    } catch {
      // ignore
    }
  }
  return THEME.LIGHT;
};

/**
 * Inline script: migrate localStorage theme → cookie (with language migrate)
 * and set `data-theme` before first paint so `auto` / dark users do not flash.
 * Reloads once only when a preference cookie had to be created from storage.
 *
 * Cookie writes use the same host-only Secure attributes as the TS writers
 * (no Domain — not sent to Galactus).
 */
export const getPreferenceCookieMigrationScript = (): string => {
  const attrs = getPreferenceCookieAttributes();
  // Verify cookie presence after write + one-shot sessionStorage guard so a
  // failed Secure write (or blocked cookies) cannot infinite-reload.
  return `(function(){try{var d=document,h=d.documentElement,c=d.cookie,wrote=false,GUARD="gail_pref_cookie_migrated",skipReload=false;
try{skipReload=!!sessionStorage.getItem(GUARD);}catch(e){}
function has(n){return c.split(";").some(function(p){return p.trim().indexOf(n+"=")===0;});}
function set(n,v){d.cookie=n+"="+encodeURIComponent(v)+"; ${attrs}";c=d.cookie;if(has(n))wrote=true;}
if(!skipReload){if(!has("${LANGUAGE_COOKIE_NAME}")){try{var raw=localStorage.getItem("userProfile");if(raw){var lang=(JSON.parse(raw).language||"").toString().toLowerCase().split("-")[0];if(lang)set("${LANGUAGE_COOKIE_NAME}",lang);}}catch(e){}}}
var pref=null;
if(has("${THEME_COOKIE_NAME}")){var m=c.match(/(?:^|; )${THEME_COOKIE_NAME}=([^;]*)/);pref=m?decodeURIComponent(m[1]):null;}
if(!pref){pref=localStorage.getItem("theme");if(!pref){try{var up=localStorage.getItem("userProfile");if(up)pref=JSON.parse(up).theme||null;}catch(e){}}if(pref==="light"||pref==="dark"||pref==="auto"){if(!skipReload)set("${THEME_COOKIE_NAME}",pref);}else{pref="light";}}
var resolved=pref==="dark"||(pref==="auto"&&window.matchMedia&&window.matchMedia("(prefers-color-scheme: dark)").matches)?"dark":"light";
h.dataset.theme=resolved;
if(wrote&&!skipReload){try{sessionStorage.setItem(GUARD,"1");}catch(e){}location.reload();}}catch(e){}})();`;
};
